import { AnyAction } from 'redux';
import { call, select } from 'redux-saga/effects';
import {
  BasisStreckenState,
  DoRequestResponse,
  GlobalActions,
  GlobalPut,
  Retry,
} from '@ps20-frontend/shop-integration-redux';
import { App } from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { Feature, TaasRequestModel, TaasResponseModel } from '@ps20/bff-models';
import { AxiosError, AxiosResponse } from 'axios';
import { CallEffect, SelectEffect } from '@redux-saga/core/effects'; // NOSONAR
import { selectFeatureToggle, selectPreisverzeichnisTestValue } from '@ps20-frontend/selector';
import { getUrlForApp, TaasData } from '../taasRequestHelper';
import { getSelectedProduktlinie } from '../preis.selector';
import { TarifierungRequestContext } from './shopTarifierungsErgebnisHandler';

export interface ExtAxiosError extends AxiosError {
  response: AxiosResponse<TaasResponseModel>;
}

export interface PricingRequestAction extends AnyAction {
  retry?: Retry;
  errorPolicy?: 'report';
}

let pricingRequestCounter = 0;

export abstract class BasisTarifierer<T extends BasisStreckenState, U extends TaasData> {
  private readonly url: string;

  constructor(
    protected appId: App,
    protected buildTaasData: (state: T) => U,
    protected shouldCancelRequest?: (state: T, action: AnyAction) => boolean,
  ) {
    this.callTarifierung = this.callTarifierung.bind(this);
    this.handleSuccess = this.handleSuccess.bind(this);
    this.handleError = this.handleError.bind(this);
    this.checkTaasCallPreconditions = this.checkTaasCallPreconditions.bind(this);
    this.getSuccessEvent = this.getSuccessEvent.bind(this);
    this.getErrorEvent = this.getErrorEvent.bind(this);
    this.buildTaasRequestModel = this.buildTaasRequestModel.bind(this);
    this.callTaas = this.callTaas.bind(this);

    this.url = getUrlForApp(this.appId);
  }

  *callTarifierung(action: AnyAction): Generator<CallEffect | SelectEffect> {
    let state: T = (yield select()) as T;
    if (yield call(this.checkTaasCallPreconditions, state, action)) {
      const taasRequestModel: TaasRequestModel<U> = (yield call(
        this.buildTaasRequestModel,
        state,
      )) as TaasRequestModel<U>;
      state = (yield select()) as T;
      this.setPreisverzeichnis(taasRequestModel, state);
      this.setVorteilsRabatt(taasRequestModel, state);
      yield call(this.callTaas, taasRequestModel, action);
    }
  }

  abstract handleSuccess(successAction: DoRequestResponse<TaasResponseModel>): Generator<unknown, void, unknown>;

  abstract handleError(errorAction: DoRequestResponse<ExtAxiosError>): Generator<unknown, void, unknown>;

  protected *callTaas(
    taasRequestModel: TaasRequestModel<U>,
    action: PricingRequestAction,
  ): Generator<CallEffect | SelectEffect> {
    const state: T = (yield select()) as T;
    const globalPut = GlobalPut.getInstance();
    const selectedProduktlinie = getSelectedProduktlinie(state.produkt);
    pricingRequestCounter += 1;
    const requestContext: TarifierungRequestContext = {
      requestedForProduktlinie: selectedProduktlinie?.produktName,
      requestedForKurztext: selectedProduktlinie?.kurztext,
      requestNumber: pricingRequestCounter,
    };
    if (!selectedProduktlinie) {
      PS20Logger.error(
        this.appId,
        'Tarifierung.keineProduktlinieSelektiert',
        JSON.stringify({
          angebotsId: state.angebotsId,
          produktlinienCount: state.produkt?.produktlinien?.length,
          produktNames: state.produkt?.produktlinien?.map(linie => linie.produktName),
          beibehalten: state.vertragsoptionen?.beibehalten,
        }),
      );
    }
    yield call(
      globalPut.put,
      new GlobalActions().doRequest({
        appId: this.appId,
        angebotsId: state.angebotsId,
        body: taasRequestModel,
        originEvent: action,
        url: this.url,
        successEvent: this.getSuccessEvent(action),
        errorEvent: this.getErrorEvent(action),
        additionalData: requestContext,
        ...(action.retry ? { retry: action.retry } : {}),
      }),
    );
  }

  protected *checkTaasCallPreconditions(state: T, action: AnyAction): Generator<CallEffect> {
    if (this.url == null) {
      PS20Logger.error(this.appId, 'ShopTarifierer: Tarifierungs-url not found');
      yield call(GlobalPut.getInstance().put, new GlobalActions().errorPageRequested());
      return false;
    }

    if (this.shouldCancelRequest?.(state, action)) {
      PS20Logger.info(this.appId, 'ShopTarifierer: Tarfierungsabbruch wurde erzwungen');
      yield call(GlobalPut.getInstance().put, new GlobalActions().errorPageRequested());
      return false;
    }

    if (state.produkt == null) {
      PS20Logger.error(this.appId, 'ShopTarifierer: Produkt im state nicht vorhanden');
      yield call(GlobalPut.getInstance().put, new GlobalActions().errorPageRequested());
      return false;
    }

    return true;
  }

  protected abstract getSuccessEvent(action: PricingRequestAction): string;

  protected abstract getErrorEvent(action: AnyAction): string;

  protected abstract buildTaasRequestModel(state: T): Generator<unknown, TaasRequestModel<U>, unknown>;

  private setPreisverzeichnis(taasRequestModel: TaasRequestModel<U>, state: T): void {
    let aktuellesPreiverzeichnis =
      state.produkt &&
      state.produkt.produktlinien &&
      state.produkt.produktlinien[0] &&
      state.produkt.produktlinien[0].preisverzeichnis;
    if (selectFeatureToggle(state, Feature.PVZ_ERSATZGESCHAEFT_MOCK)) {
      aktuellesPreiverzeichnis = selectPreisverzeichnisTestValue(state);
    }
    const priceGuarantee = state.transientState.priceGuarantee;
    const isErsatzgeschaeft =
      state.vertragsoptionen.vorvertragsNummern &&
      state.vertragsoptionen.vorvertragsNummern.length > 0;

    const isBeibehalten = state.vertragsoptionen.beibehalten;
    const hasPriceGuaranteeForNeugeschaeft = !isErsatzgeschaeft && priceGuarantee === true;
    const hasPriceGuaranteeForErsatzgeschaeft = isErsatzgeschaeft && priceGuarantee;

    const shouldPreserveCurrentPreisVerzeichniss =
      aktuellesPreiverzeichnis != null &&
      (hasPriceGuaranteeForNeugeschaeft || isBeibehalten || hasPriceGuaranteeForErsatzgeschaeft);

    if (shouldPreserveCurrentPreisVerzeichniss) {
      taasRequestModel.preisverzeichnis = aktuellesPreiverzeichnis;
    }
  }

  private setVorteilsRabatt(taasRequestModel: TaasRequestModel<U>, state: T): void {
    taasRequestModel.taasSparte.internerVertragsbezugs = state.taasInfo?.internerVertragsbezugs;
  }
}
